import { Link } from "react-router-dom";
import { LANDING } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import {
  ArrowUpRight,
  FileText,
  Landmark,
  Sparkles,
  Globe,
  ShieldCheck,
} from "lucide-react";

const currencyMarquee = [
  "USD", "EUR", "GBP", "ZAR", "NGN", "KES", "GHS", "INR",
  "AUD", "CAD", "JPY", "AED", "ZMW", "UGX", "TZS", "EGP",
];

const features = [
  {
    icon: FileText,
    title: "Grant proposals in minutes",
    body: "Structured, funder-ready documents with objectives, budget and M&E baked in.",
  },
  {
    icon: Landmark,
    title: "Four industry loan tracks",
    body: "Construction, Manufacturing, Fintech and Commercial — tuned rates, tenures and docs.",
  },
  {
    icon: Sparkles,
    title: "Custom AI writing",
    body: "Claude-powered proposals that reflect your revenue, purpose and repayment strategy.",
  },
  {
    icon: Globe,
    title: "16+ international currencies",
    body: "From USD and EUR to ZAR, NGN, KES and INR — write in the currency of the deal.",
  },
];

export default function Landing() {
  return (
    <div className="w-full">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="text-xs tracking-[0.28em] uppercase text-secondary font-semibold mb-6">
              Imali · Money for growth
            </p>
            <h1 className="font-display font-light text-5xl lg:text-7xl leading-[0.95] tracking-tighter text-foreground">
              Write grants.
              <br />
              <span className="text-primary">Apply for loans.</span>
              <br />
              <span className="italic font-normal">In one calm workspace.</span>
            </h1>
            <p className="mt-8 max-w-xl text-lg leading-relaxed text-muted-foreground">
              Imali drafts lender-ready and funder-ready proposals for
              construction, manufacturing, fintech and commercial businesses —
              across USD, EUR, ZAR, NGN, KES, INR and 10+ more currencies.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/proposals/new?type=grant" data-testid={LANDING.heroWriteGrant}>
                <Button
                  size="lg"
                  className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-7 h-12"
                >
                  Write a grant proposal
                  <ArrowUpRight aria-hidden="true" className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/loans" data-testid={LANDING.heroApplyLoan}>
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full border-foreground/20 hover:border-foreground/40 px-7 h-12"
                >
                  Apply for a business loan
                </Button>
              </Link>
              <Link to="/compare" className="text-sm underline-offset-4 hover:underline text-primary font-medium ml-1">
                or compare 2–4 banking systems →
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-border imali-grain">
              <img
                src="https://images.unsplash.com/photo-1687422808248-f807f4ea2a2e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxhZnJpY2FuJTIwYnVzaW5lc3MlMjBvd25lciUyMGVudHJlcHJlbmV1cnxlbnwwfHx8fDE3ODQzMjg4NDR8MA&ixlib=rb-4.1.0&q=85"
                alt="Business owner reviewing an Imali proposal"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-x-6 bottom-6 bg-background/85 backdrop-blur-md border border-border rounded-2xl p-5 shadow-sm">
                <div className="flex items-center gap-3">
                  <ShieldCheck aria-hidden="true" className="w-5 h-5 text-primary" />
                  <span className="text-sm font-medium">
                    Lender-ready. Reviewable. Yours to edit.
                  </span>
                </div>
                <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
                  Every proposal ships with executive summary, use of funds,
                  DSCR reasoning and repayment logic.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Currency marquee */}
        <div className="border-y border-border bg-muted/40 overflow-hidden">
          <div className="max-w-7xl mx-auto flex items-center gap-4 py-4 px-6 lg:px-10">
            <span className="text-[10px] tracking-[0.28em] uppercase text-muted-foreground shrink-0">
              Supported currencies
            </span>
            <div className="flex-1 overflow-hidden">
              <div className="imali-marquee flex whitespace-nowrap w-max font-mono text-sm text-foreground/70">
                {[...currencyMarquee, ...currencyMarquee].map((c, i) => (
                  <span key={i} className="mx-6">
                    {c}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {features.map((f, i) => {
          const Icon = f.icon;
          return (
            <div
              key={f.title}
              data-testid={LANDING.featureCard(i)}
              className="group p-6 rounded-2xl border border-border bg-card hover:-translate-y-1 hover:shadow-md transition-transform duration-200"
            >
              <Icon aria-hidden="true" className="w-6 h-6 text-primary" />
              <h3 className="mt-6 font-display text-xl tracking-tight">
                {f.title}
              </h3>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                {f.body}
              </p>
            </div>
          );
        })}
      </section>

      {/* Call to industry hub */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="relative overflow-hidden rounded-3xl border border-border bg-primary text-primary-foreground p-10 lg:p-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <p className="text-xs tracking-[0.28em] uppercase text-secondary font-semibold mb-4">
                Business loan tracks
              </p>
              <h2 className="font-display text-3xl lg:text-5xl tracking-tighter font-light leading-tight">
                Four industry loan plans, tuned to how your business actually
                earns.
              </h2>
              <p className="mt-5 max-w-2xl text-primary-foreground/80 leading-relaxed">
                Compare amount envelopes, tenures, interest ranges and required
                documents for Construction, Manufacturing, Fintech and
                Commercial businesses — then generate a tailored proposal.
              </p>
            </div>
            <div className="lg:col-span-4 lg:justify-self-end">
              <Link to="/loans">
                <Button
                  size="lg"
                  className="rounded-full bg-secondary text-secondary-foreground hover:bg-secondary/90 px-7 h-12"
                >
                  Explore loan plans
                  <ArrowUpRight aria-hidden="true" className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
